<template>
	<v-list-item class="connector-item mb-2">
		<v-list-item-content>
			<v-list-item-title class="d-flex align-center">
				<v-chip color="secondary" x-small class="mr-2">
					{{ row.connectorLabel || row.type }}
				</v-chip>
				<v-chip :color="row.enabled ? 'primary' : 'error'" x-small class="mr-2">
					{{ row.enabled ? $t("enabled") : $t("disabled") }}
				</v-chip>
				<span v-if="row.organizationName" class="font-weight-medium">
					{{ row.organizationName }}
				</span>
				<v-spacer />
				<v-menu offset-y left>
					<template #activator="{ on, attrs }">
						<v-btn icon x-small v-bind="attrs" v-on="on">
							<v-icon small>mdi-dots-vertical</v-icon>
						</v-btn>
					</template>
					<v-list dense>
						<v-list-item @click="$emit('update')">
							<v-list-item-icon class="mr-2">
								<v-icon small color="primary">mdi-pencil</v-icon>
							</v-list-item-icon>
							<v-list-item-title>{{ $t("connector.update-title") }}</v-list-item-title>
						</v-list-item>
						<v-list-item @click="$emit('toggle-enabled')">
							<v-list-item-icon class="mr-2">
								<v-icon small :color="row.enabled ? 'error' : 'success'">mdi-power</v-icon>
							</v-list-item-icon>
							<v-list-item-title>
								{{ row.enabled ? $t("connector.disable-title") : $t("connector.enable-title") }}
							</v-list-item-title>
						</v-list-item>
						<v-list-item @click="$emit('delete')">
							<v-list-item-icon class="mr-2">
								<v-icon small color="error">mdi-delete</v-icon>
							</v-list-item-icon>
							<v-list-item-title>{{ $t("connector.delete-title") }}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</v-list-item-title>
			<v-list-item-subtitle class="mt-1">
				<div class="d-flex flex-column">
					<span class="text-caption grey--text">
						<strong>{{ $t("connector.crm-id") }}:</strong> {{ row.crmId }}
						<v-icon small class="ml-1 copy-icon" @click.stop="emitCopy(row.crmId)">mdi-content-copy</v-icon>
					</span>
					<span class="text-caption grey--text">
						<strong>{{ $t("organization-id") }}:</strong> {{ row.organizationId }}
						<v-icon small class="ml-1 copy-icon" @click.stop="emitCopy(row.organizationId)">mdi-content-copy</v-icon>
					</span>
					<span class="text-caption grey--text">
						<strong>ID:</strong> {{ row.id }}
						<v-icon small class="ml-1 copy-icon" @click.stop="emitCopy(row.id)">mdi-content-copy</v-icon>
					</span>
					<span v-if="row.username" class="text-caption grey--text d-flex align-center">
						<strong>{{ $t("connector.username") }}:</strong>
						<span class="ml-1">{{ showUsername ? row.username : "••••••" }}</span>
						<v-btn icon x-small class="ml-1" @click.stop="showUsername = !showUsername">
							<v-icon small>{{ showUsername ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
						</v-btn>
						<v-icon v-if="showUsername" small class="ml-1 copy-icon" @click.stop="emitCopy(row.username)">mdi-content-copy</v-icon>
					</span>
					<span v-if="row.password" class="text-caption grey--text d-flex align-center">
						<strong>{{ $t("connector.password") }}:</strong>
						<span class="ml-1">{{ showPassword ? row.password : "••••••" }}</span>
						<v-btn icon x-small class="ml-1" @click.stop="showPassword = !showPassword">
							<v-icon small>{{ showPassword ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
						</v-btn>
						<v-icon v-if="showPassword" small class="ml-1 copy-icon" @click.stop="emitCopy(row.password)">mdi-content-copy</v-icon>
					</span>
					<span class="text-caption grey--text d-flex align-center">
						<v-btn icon x-small class="mr-1" @click.stop="$emit('toggle-sessions')">
							<v-icon small>{{ expandedSessions ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
						</v-btn>
						<strong>{{ $t("connector.sessions") }}</strong>
						<v-progress-circular
							v-if="sessionsLoading"
							indeterminate
							size="12"
							width="2"
							class="ml-2"
						/>
					</span>
					<div v-if="expandedSessions" class="ml-4 mt-1">
						<div v-if="!sessionsLoading && sessions.length === 0" class="text-caption grey--text">
							{{ $t("connector.sessions-empty") }}
						</div>
						<div v-for="s in sessions" :key="s.id" class="session-item text-caption grey--text">
							<div>
								<strong>ID:</strong> {{ s.id }}
								<v-icon small class="ml-1 copy-icon" @click.stop="emitCopy(s.id)">mdi-content-copy</v-icon>
							</div>
							<div v-if="s.userId">
								<strong>userId:</strong> {{ s.userId }}
							</div>
							<div v-if="s.userGroupId">
								<strong>userGroupId:</strong> {{ s.userGroupId }}
							</div>
							<div>
								<strong>expiresAt:</strong> {{ s.expiresAt || "-" }}
								<v-chip v-if="isExpired(s)" x-small color="error" class="ml-1">
									{{ $t("expired") }}
								</v-chip>
							</div>
						</div>
					</div>
				</div>
			</v-list-item-subtitle>
		</v-list-item-content>
	</v-list-item>
</template>

<script>
export default {
	name: "ConnectorRow",

	props: {
		row: { type: Object, required: true },
		expandedSessions: { type: Boolean, default: false },
		sessionsLoading: { type: Boolean, default: false },
		sessions: { type: Array, default: () => []}
	},

	data() {
		return {
			showUsername: false,
			showPassword: false
		};
	},

	methods: {
		isExpired(session) {
			return (
				session.expiresAt && new Date(session.expiresAt).getTime() < Date.now()
			);
		},

		emitCopy(text) {
			this.$emit("copy", text);
		}
	}
};
</script>

<style scoped>
.connector-item {
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 8px;
}

.copy-icon {
	cursor: pointer;
	opacity: 0.6;
}

.copy-icon:hover {
	opacity: 1;
}

.session-item {
	padding: 4px 0;
	border-top: 1px dashed #eee;
}
</style>
